import { atom } from 'recoil';
import { getDownloadedTours, type DownloadedToursMap } from '@app/pages/delete-dbs';

export type CredentialItems = {
    uID?: string;
    tID?: string;
    gID?: string;
    lastVisit?: string;
};

// Cookie helpers - shared between Safari and iOS PWA (which has separate localStorage)
function setCredentialsCookie(credentials: CredentialItems) {
    try {
        const value = encodeURIComponent(JSON.stringify(credentials));
        const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `dguide_credentials=${value};expires=${expires};path=/;SameSite=Lax`;
    } catch (e) {
        // ignore
    }
}

function getCredentialsCookie(): CredentialItems {
    try {
        const match = document.cookie.match(/(?:^|;\s*)dguide_credentials=([^;]*)/);
        if (match) {
            return JSON.parse(decodeURIComponent(match[1]));
        }
    } catch (e) {
        // ignore
    }
    return {};
}

/** Save credentials to both localStorage and cookie (for iOS PWA support) */
export function saveCredentials(credentials: CredentialItems) {
    // Preserve lastVisit if not provided
    if (!credentials.lastVisit) {
        try {
            const existing = JSON.parse(localStorage.getItem('credential_items') || '{}');
            if (existing.lastVisit) {
                credentials.lastVisit = existing.lastVisit;
            }
        } catch (e) { /* ignore */ }
    }
    localStorage.setItem('credential_items', JSON.stringify(credentials));
    setCredentialsCookie(credentials);
}

/** Load credentials from localStorage, falling back to cookie for iOS PWA */
export function loadCredentials(): CredentialItems {
    let credentials: CredentialItems = {};
    try {
        const raw = localStorage.getItem('credential_items');
        credentials = raw ? JSON.parse(raw) : {};
        if (credentials.tID || credentials.uID) {
            // Sync to cookie
            setCredentialsCookie(credentials);
        }
    } catch (e) {
        // ignore
    }
    // Fallback: try cookie
    if (!credentials.tID && !credentials.uID) {
        const fromCookie = getCredentialsCookie();
        if (fromCookie.tID || fromCookie.uID) {
            credentials = fromCookie;
        }
    }
    // Check download freshness before updating lastVisit
    const oldLastVisit = credentials.lastVisit;
    const today = new Date().toISOString().split('T')[0];

    if (oldLastVisit) {
        const daysSinceLastVisit = Math.floor(
            (new Date(today).getTime() - new Date(oldLastVisit).getTime()) / (1000 * 60 * 60 * 24)
        );
        const tours = getDownloadedTours();
        if (daysSinceLastVisit < 7) {
            // User visited within 7 days — refresh download timestamps
            const updatedTours: DownloadedToursMap = {};
            for (const [tourId, info] of Object.entries(tours)) {
                updatedTours[tourId] = { ...info, timestamp: new Date(today).getTime() };
            }
            if (Object.keys(updatedTours).length > 0) {
                localStorage.setItem('downloaded_tours', JSON.stringify(updatedTours));
            }
        }
        // If gap >= 7 days, download timestamps stay old (stale)
    }

    // Update lastVisit
    credentials.lastVisit = today;
    saveCredentials(credentials);
    return credentials;
}

const getInitialCredentialItems = (): CredentialItems => {
    return loadCredentials();
};

export const atom_credentialItemsState = atom<CredentialItems>({
    key: 'CredentialItems',
    default: getInitialCredentialItems(),
});
